import React, { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import Icon from '../AppIcon';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n/config';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Reusable Language Selector Component
 * Works on all pages (auth, public, protected)
 */
const LanguageSelector = ({ className = '' }) => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setIsDropdownOpen(false);
  };

  const renderFlag = (isoCode) => {
    const colorSchemes = {
      GB: 'from-blue-500 to-red-500',
      UA: 'from-blue-400 to-yellow-400',
      RU: 'from-blue-600 to-red-600',
      TH: 'from-red-500 to-blue-600',
      DE: 'from-yellow-400 to-red-500',
      FR: 'from-blue-600 to-red-500',
      CN: 'from-red-600 to-yellow-400',
      ES: 'from-yellow-400 to-red-600',
      IL: 'from-blue-500 to-white',
      NL: 'from-red-500 to-blue-600',
      IT: 'from-green-500 to-red-500'
    };

    const gradient = colorSchemes?.[isoCode] || 'from-gray-400 to-gray-600';

    return (
      <span 
        className={`inline-flex items-center justify-center w-6 h-6 bg-gradient-to-br ${gradient} text-white text-[9px] font-bold rounded-full shadow-md border border-white transform hover:scale-110 transition-transform duration-200`}
      >
        {isoCode}
      </span>
    );
  };

  const currentLanguageDetails = SUPPORTED_LANGUAGES?.find((lang) => lang?.code === currentLanguage);

  return (
    <div className={`relative w-28 xs:w-32 ${className}`}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center justify-between w-full gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border-2 border-gray-200 hover:border-gray-300 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
        aria-label="Select language"
      >
        <div className="flex items-center gap-2 min-w-0">
          {renderFlag(currentLanguageDetails?.flag, currentLanguageDetails?.code)}
          <span className="font-medium truncate hidden xs:inline">{currentLanguageDetails?.name}</span>
        </div>
        <Icon name="ChevronDown" size={16} className="flex-shrink-0" />
      </button>

      {isDropdownOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsDropdownOpen(false)}
          />
          <div className="absolute right-0 xs:right-0 top-full mt-2 w-48 max-w-[calc(100vw-2rem)] bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-lg shadow-2xl overflow-hidden z-50 max-h-80 overflow-y-auto">
            {SUPPORTED_LANGUAGES?.map((lang) => (
              <button
                key={lang?.code}
                onClick={() => handleLanguageChange(lang?.code)}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs transition-all duration-200 transform hover:scale-[1.02] hover:-translate-y-0.5 ${
                  currentLanguage === lang?.code 
                    ? 'text-amber-600 bg-gradient-to-r from-amber-50 to-amber-100 shadow-md font-semibold border-l-4 border-amber-500' 
                    : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-150 shadow-sm hover:shadow-md'
                }`}
              >
                <span className="flex items-center gap-1.5 flex-1">
                  {renderFlag(lang?.flag, lang?.code)}
                  <span>{lang?.name}</span>
                </span>
                {currentLanguage === lang?.code && (
                  <Icon name="Check" size={14} className="ml-auto flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;